import React, { useEffect, useState } from "react";
import { Table, Input, Button, Space, Popconfirm, message, Tag, Form, Select } from "antd";
import { EditOutlined, DeleteOutlined, FilterOutlined } from "@ant-design/icons";
import { Helmet } from "react-helmet";
import startCase from "lodash/startCase";
import { Typography } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import BrandForm from "../components/Brands/BrandForm";
import RequirePermission from "../components/Common/RequirePermission";
const { Title } = Typography;

const Brands = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);

  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const [createPermission, setCreatePermission] = useState(false);
  const [editPermission, setEditPermission] = useState(false);
  const [deletePermission, setDeletePermission] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filterText, setFilterText] = useState("");

  const handleSearch = (e) => {
    setFilterText(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusChange = (value) => {
    setSelectedStatus(value || "");
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilterText("");
    setSelectedStatus("");
    setCurrentPage(1);
  };

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const fetchMasterData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/brands/masters`);
      setStatusOptions(response.data.data.status);
    } catch (err) {
      console.error(err.message);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(process.env.REACT_APP_BASE_URL + `/api/brands`, {
        params: {
          filter: filterText,
          status: selectedStatus,
          limit: pageSize,
          page: currentPage,
        },
      });
      setTotalRecords(response.data.data.total);
      setBrands(response.data.data.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching brands data", error);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`${process.env.REACT_APP_BASE_URL}/api/brands/${id}`);
      const updated_row_count = response.data.data.updated_row_count;
      if (updated_row_count === 1) {
        message.success("Brand deleted");
        await fetchData();
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("userData"));
    if (storedUser) {
      const permissions = storedUser.permissions;
      setCreatePermission(parseInt(permissions["brands.create"]) === 1);
      setEditPermission(parseInt(permissions["brands.edit"]) === 1);
      setDeletePermission(parseInt(permissions["brands.delete"]) === 1);
    }
    fetchMasterData();
  }, []);

  useEffect(() => {
    const fetchTimer = setTimeout(() => {
      fetchData();
    }, 500);
    return () => clearTimeout(fetchTimer);
  }, [currentPage, pageSize, filterText, selectedStatus]);

  const columns = [
    { title: "Title", dataIndex: "title" },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <Tag color={status === "active" ? "green" : "red"}>
          {status === "active" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      render: (text) => <span className="text-small">{dayjs(text).format("DD-MM-YYYY HH:mm")}</span>,
    },
    {
      title: "Created By",
      dataIndex: "created_by_user",
      render: (text) => <span className="text-small">{startCase(text)}</span>,
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
      render: (text) => <span className="text-small">{text ? dayjs(text).format("DD-MM-YYYY HH:mm") : "-"}</span>,
    },
    {
      title: "Updated By",
      dataIndex: "updated_by_user",
      render: (text) => <span className="text-small">{startCase(text)}</span>,
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          {editPermission && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingBrand(record);
                setIsModalOpen(true);
              }}
            />
          )}
          {deletePermission && (
            <Popconfirm
              title="Are you sure to delete this brand?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button danger size="small" icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <RequirePermission permission="brands.view">
      <div style={{ padding: "1rem" }}>
        <Helmet>
          <title>{`${process.env.REACT_APP_NAME} | Brands`}</title>
        </Helmet>
        <Title level={3} style={{ marginBottom: "1rem" }}>
          Brands
        </Title>
        <div className="d-sm-block d-md-flex justify-content-between mb-3">
          <Form layout="inline">
            <Form.Item>
              <Input.Search
                placeholder="Search brand"
                value={filterText}
                className="me-1"
                onChange={handleSearch}
                style={{ width: 200 }}
              />
            </Form.Item>
            <Form.Item>
              <Button
                icon={<FilterOutlined />}
                type={showFilters ? "primary" : "default"}
                onClick={() => setShowFilters((prev) => !prev)}
              >
                Filters
                {selectedStatus && (
                  <Tag color="blue" style={{ marginLeft: 6, marginRight: 0 }}>
                    1
                  </Tag>
                )}
              </Button>
            </Form.Item>
          </Form>
          {createPermission && (
            <Button
              type="primary"
              onClick={() => {
                setEditingBrand(null);
                setIsModalOpen(true);
              }}
            >
              Add New Brand
            </Button>
          )}
        </div>

        {showFilters && (
          <Form layout="inline" className="mb-3">
            <Form.Item label="Status">
              <Select
                value={selectedStatus}
                allowClear
                onChange={handleStatusChange}
                style={{ width: 160 }}
              >
                {statusOptions.map((status) => (
                  <Select.Option key={status.value} value={status.value}>
                    {status.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item>
              <Button onClick={handleClearFilters}>Clear Filters</Button>
            </Form.Item>
          </Form>
        )}

        <Table
          loading={loading}
          columns={columns}
          dataSource={brands}
          scroll={{ x: "max-content" }}
          rowKey={(record) => record.id}
          onChange={handleTableChange}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRecords,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} brands`,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50", "100"],
          }}
        />

        {isModalOpen && (
          <BrandForm
            isModalOpen={isModalOpen}
            editingBrand={editingBrand}
            closeModal={() => setIsModalOpen(false)}
            handleUpdateList={() => fetchData()}
          />
        )}
      </div>
    </RequirePermission>
  );
};

export default Brands;
